/**
 * Favorites module — ported from Designer
 * Supports both anonymous (session-based) and logged-in users
 */

import { store } from '../../../core/store.js'
import { CONFIG } from '../../../core/config.js'
import { eventBus, EVENTS } from '../../../core/event-bus.js'
import { createLogger } from '../../../utils/logger.js'
import { fetchWithTimeout, getSessionId } from '../../../api/index.js'
import { safeImageUrl } from '../../../utils/escape.js'

const log = createLogger('favorites')

// Module state
const state = {
  favorites: [],
  loading: false,
  showingFavorites: false,
  columns: 2,
  columnHeights: [0, 0],
}

/**
 * Initialize favorites (load from server)
 */
export async function init() {
  log.info('Favorites Manager initialized')
  try {
    await fetchFavorites()
    log.info('Favorites loaded:', state.favorites.length)
  } catch (error) {
    log.error('Failed to load favorites:', error)
  }
}

/**
 * Fetch favorites from API
 */
async function fetchFavorites(limit = 20, offset = 0) {
  if (state.loading) return
  state.loading = true

  try {
    const token = store.getState('auth.token')
    const headers = {}
    if (token) headers['Authorization'] = `Bearer ${token}`

    const sessionId = getSessionId()
    const url = token
      ? `${CONFIG.CUSTOMER_API}/api/favorites?limit=${limit}&offset=${offset}`
      : `${CONFIG.CUSTOMER_API}/api/favorites?session_id=${sessionId}&limit=${limit}&offset=${offset}`

    const response = await fetchWithTimeout(url, { headers })
    const result = await response.json()
    if (!response.ok) throw new Error(result.error || 'Failed to load favorites')

    const favorites = result.data || result.favorites || []
    state.favorites = offset === 0 ? favorites : state.favorites.concat(favorites)
    return { ...result, favorites }
  } catch (error) {
    log.error('Failed to load favorites:', error)
    throw error
  } finally {
    state.loading = false
  }
}

/**
 * Add a favorite
 */
export async function addFavorite(imageData) {
  const { image_url, item_id, source_title, item_title } = imageData
  if (!image_url && !item_id) throw new Error('image_url or item_id is required')

  const resolvedItemId = item_id || image_url
  const resolvedTitle = item_title || source_title || null

  const token = store.getState('auth.token')
  const headers = { 'Content-Type': 'application/json' }
  if (token) headers['Authorization'] = `Bearer ${token}`

  const response = await fetchWithTimeout(`${CONFIG.CUSTOMER_API}/api/favorites`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      item_type: 'image',
      item_id: resolvedItemId,
      item_title: resolvedTitle,
      item_image_url: image_url || null,
      session_id: token ? undefined : getSessionId(),
    }),
  })

  const result = await response.json()
  if (!response.ok) throw new Error(result.error || 'Failed to add favorite')

  const newId = result.data?.id || result.favorite_id
  state.favorites.unshift({
    id: newId,
    item_id: resolvedItemId,
    item_type: 'image',
    item_image_url: image_url,
    image_url,
    item_title: resolvedTitle,
    created_at: new Date().toISOString(),
  })

  eventBus.emit(EVENTS.FAVORITE_ADD, { favorite_id: newId, image_url })
  return result
}

/**
 * Remove a favorite
 */
export async function removeFavorite(favoriteId) {
  const token = store.getState('auth.token')
  const headers = {}
  if (token) headers['Authorization'] = `Bearer ${token}`

  const url = token
    ? `${CONFIG.CUSTOMER_API}/api/favorites/${favoriteId}`
    : `${CONFIG.CUSTOMER_API}/api/favorites/${favoriteId}?session_id=${getSessionId()}`

  const response = await fetchWithTimeout(url, { method: 'DELETE', headers })
  const result = await response.json()
  if (!response.ok) throw new Error(result.error || 'Failed to remove favorite')

  state.favorites = state.favorites.filter((f) => f.id !== favoriteId)
  eventBus.emit(EVENTS.FAVORITE_REMOVE, { favorite_id: favoriteId })
  return result
}

/**
 * Check whether an item is favorited (by ID)
 */
export function isFavorite(materialId) {
  return state.favorites.some(
    (f) => f.id === materialId || f.item_id === materialId || f.image_id === materialId
  )
}

/**
 * Toggle favorite state with optimistic update
 */
export async function toggleFavorite(material) {
  const isFav = isFavorite(material.id)
  const snapshot = [...state.favorites]

  if (isFav) {
    const favorite = state.favorites.find((f) => f.id === material.id || f.image_id === material.id)
    if (!favorite) return false

    state.favorites = state.favorites.filter(
      (f) => f.id !== favorite.id && f.image_id !== material.id
    )
    try {
      await removeFavorite(favorite.id)
    } catch (err) {
      state.favorites = snapshot
      eventBus.emit(EVENTS.TOAST_SHOW, { message: 'Failed to remove favorite', type: 'error' })
    }
    return false
  } else {
    const placeholder = {
      id: `optimistic_${material.id}`,
      image_id: material.id,
      created_at: new Date().toISOString(),
    }
    state.favorites.unshift(placeholder)
    try {
      await addFavorite({
        image_url: material.url || material.thumbnail_url || material.item_image_url,
        item_id: material.id || material.item_id,
        item_title: material.title || material.item_title,
      })
      state.favorites = state.favorites.filter((f) => f.id !== placeholder.id)
    } catch (err) {
      state.favorites = snapshot
      eventBus.emit(EVENTS.TOAST_SHOW, { message: 'Failed to add favorite', type: 'error' })
    }
    return true
  }
}

/**
 * Get favorites list
 */
export function getFavorites() {
  return state.favorites
}

/**
 * Render favorites in waterfall grid
 */
export function renderFavorites() {
  const grid = document.getElementById('materialsGrid')
  if (!grid) return

  if (state.favorites.length === 0) {
    grid.innerHTML =
      '<div class="placeholder-text">No favorites yet<br><span style="font-size:12px;opacity:0.6;">Click the heart icon on images to add them</span></div>'
    return
  }

  grid.innerHTML = ''
  const container = document.createElement('div')
  container.className = 'waterfall-container'

  for (let i = 0; i < state.columns; i++) {
    const column = document.createElement('div')
    column.className = 'waterfall-column'
    column.id = `fav-col-${i}`
    container.appendChild(column)
  }

  grid.appendChild(container)
  state.columnHeights = new Array(state.columns).fill(0)

  state.favorites.forEach((favorite, idx) => {
    const item = document.createElement('div')
    item.className = 'material-item'
    item.style.position = 'relative'

    item.onclick = (e) => {
      if (e.target.closest('.fav-toggle-btn')) return
      const imgUrl = safeImageUrl(favorite.item_image_url || favorite.image_url)
      if (window.MaterialManager?.addMaterialToDesign && imgUrl) {
        window.MaterialManager.addMaterialToDesign(
          imgUrl,
          favorite.id,
          favorite.item_title || favorite.source_title
        )
      }
    }

    const favBtn = document.createElement('button')
    favBtn.className = 'fav-toggle-btn active'
    favBtn.innerHTML = '<i class="ph ph-heart-fill" style="font-size:14px;"></i>'
    favBtn.onclick = (e) => {
      e.stopPropagation()
      removeFavorite(favorite.id).then(() => {
        eventBus.emit(EVENTS.TOAST_SHOW, {
          message: 'Removed from favorites',
          type: 'info',
          duration: 1500,
        })
        renderFavorites()
      })
    }
    item.appendChild(favBtn)

    const img = document.createElement('img')
    const imgUrl = safeImageUrl(favorite.item_image_url || favorite.image_url)
    if (!imgUrl) return
    img.src = imgUrl
    img.loading = idx < 12 ? 'eager' : 'lazy'
    img.alt = favorite.item_title || 'Favorite'
    img.onerror = function () {
      this.src =
        'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="150" height="200"%3E%3Crect fill="%23f5f5f5" width="150" height="200"/%3E%3C/svg%3E'
    }
    item.appendChild(img)

    const shortestColIndex = state.columnHeights.indexOf(Math.min(...state.columnHeights))
    const column = document.getElementById(`fav-col-${shortestColIndex}`)
    if (column) {
      column.appendChild(item)
      state.columnHeights[shortestColIndex] += 200 + 12
    }
  })
}

// Reload favorites after login
eventBus.on(EVENTS.USER_LOGIN, () => {
  fetchFavorites().catch((err) => log.warn('Failed to reload favorites after login:', err))
})

// Expose for backward compat
if (typeof window !== 'undefined') {
  window.FavoritesManager = {
    state,
    init,
    addFavorite,
    removeFavorite,
    isFavorite,
    toggleFavorite,
    getFavorites,
    renderFavorites,
  }
}

export { state }
export default {
  init,
  addFavorite,
  removeFavorite,
  isFavorite,
  toggleFavorite,
  getFavorites,
  renderFavorites,
  state,
}
